import React, { useCallback } from 'react'
import { GestureResponderEvent } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { NFModel } from '@/domain/models'
import { OpenNfse, OpenNfseParams } from '@/domain/usecases'
import { DateAdapter } from '@/data/protocols'
import { MenuActions, Menu, Box, IconButton, Text, CardList, PageHeader } from '@/presentation/components'
import { CardNF, nfsState, isLoadingNfState, nfsConfigState, isOpenNfsFiltersState, FiltersChipPanel } from '@/presentation/pages/NotaFiscal/components'
import { useErrorHandler, useFormat, useNotify, useTheme } from '@/presentation/hooks'
import { styles } from '../../styles'

type NFServiceProps = {
  openNfse: OpenNfse
  dateAdapter: DateAdapter
}

export const NFService: React.FC<NFServiceProps> = ({ openNfse, dateAdapter }) => {
  const theme = useTheme()
  const notify = useNotify()
  const { formatCurrency } = useFormat()
  const [currentIndex, setCurrentIndex] = React.useState(-1)
  const [isLoading, setLoading] = useRecoilState(isLoadingNfState)
  const nfsConfigs = useRecoilValue(nfsConfigState)
  const nfs = useRecoilValue(nfsState)
  const setOpenFilters = useSetRecoilState(isOpenNfsFiltersState)
  const handleError = useErrorHandler()

  const openMenu = (index: number): void => setCurrentIndex(index)
  const closeMenu = (): void => setCurrentIndex(-1)

  const handleOpenNfse = useCallback((event: GestureResponderEvent, nf: NFModel) => {
    event.preventDefault()
    const openNfseParams: OpenNfseParams = {
      codEmpresa: nf.empresa,
      codUnidade: nf.unidade,
      numeroRPS: nf.notaNumero,
      serieRps: nf.notaSerie,
      linkAutenticidade: nf.linkAutenticidade,
      utilizarDevExpress: nf.utilizarDevExpress
    }

    openNfse
      .open(openNfseParams)
      .then(() => notify.show(`A Nfse ${nf.notaNumero}/${nf.notaSerie} foi aberta em uma nova aba`))
      .catch(handleError)
      .finally(() => setLoading(false))
  }, [])

  const handleOnFilter = useCallback((): void => {
    setOpenFilters(true)
  }, [])

  return (
    <ScrollView style={styles.scroll}>
      <Box style={styles.content}>
        <PageHeader
          icon={<MaterialCommunityIcons size={24} color={theme.colors.grey} name='file-document-outline' />}
          text='Notas Fiscais de Serviço'
          openFilters={handleOnFilter}
        />
        <FiltersChipPanel dateAdapter={dateAdapter} />
        <CardList
          isEmpty={!nfs?.length}
          isLoading={isLoading}
          textLoading='Buscando Notas Fiscais Serviço'
        >
          {nfs?.map((nfse, index) => (
            <CardNF key={nfse.notaNumeroNFSE}>
              <Box style={styles.title}>
                <Text style={styles.bold}>{nfse.notaNumeroNFSE}/{nfse.notaSerie}</Text>
                <MenuActions
                  key={`menu-${nfse.notaNumeroNFSE}`}
                  visible={currentIndex >= 0 && currentIndex === index}
                  onDismiss={closeMenu}
                  anchor={<IconButton
                    onPress={() => openMenu(index)}
                    style={{ width: 32, height: 32 }}
                    icon={(props: any) => <MaterialCommunityIcons name="dots-vertical" {...props} />}></IconButton>
                  }>
                    {((nfse.linkAutenticidade || nfse.utilizarDevExpress) && !nfsConfigs.ocultaLink)
                      ? <Menu.Item
                        onPress={(event) => handleOpenNfse(event, nfse)}
                        title="Abrir Link NFSe"
                        leadingIcon={props => <MaterialCommunityIcons name="link" {...props} />}
                      />
                      : <></>
                    }
                    {nfse.linkConsultaNotaPortal
                      ? <Menu.Item
                        onPress={() => window.open(nfse.linkConsultaNotaPortal, '_blank')}
                        title="Link para consulta da NFSe"
                        leadingIcon={props => <MaterialCommunityIcons name="link" {...props} />}
                      />
                      : <></>
                    }
                </MenuActions>
              </Box>
              <Text><span style={styles.bold}>Valor: </span>{formatCurrency(nfse.notaValor)}</Text>
              <Text><span style={styles.bold}>Emissão: </span>{dateAdapter.format(dateAdapter.parse(nfse.notaData), 'DD/MM/YYYY')}</Text>
            </CardNF>
          ))}
        </CardList>
      </Box>
    </ScrollView>
  )
}
